import type { FileInfo, StandardResponse, StreamInput } from "@pdfking/io"
import { ResponseAdopt, ResponseCreate, UInt8Collect } from "@pdfking/io"
import type { Bytes, ContentType, FileName } from "@pdfking/structs"
import { NullQ } from "@pdfking/structs"
import { WaitPromises, Zip, type AsyncSequence } from "@pdfking/tfunc"

import type { FileQuery, FileSystem } from "#public"

import { EntryKey } from "../../internal/EntryKey"
import { FolderMemberQ } from "../../internal/FolderMemberQ"

// One spelling per entry — see EntryKey — so the url a write is stored at
// is the one a read looks for.
const to_url = (key: FileName): string => `https://cache/${EntryKey(key)}`
const from_url = (url: string): FileName =>
  decodeURIComponent(new URL(url).pathname.slice(1)) as FileName

/** The filesystem of a browser: paths in Cache storage, one entry per file,
 * written where they are named.
 *
 * The path is the key and the entry is the whole file — no index beside it,
 * because an index is a second thing to keep true. What the Cache API cannot
 * do is range over its keys, so a folder is the full listing narrowed by the
 * same walk rules every flat-keyed storage here uses: the cost of the one
 * storage that has no order to it.
 *
 * Nothing wraps this: a put replaces the entry atomically on its own, so a
 * write here is already the whole thing a reader can find.
 *
 * `name` is the cache — everything this holds is in it. */
export const BrowserFileSystem = (name: string): FileSystem => {
  // Opened once, on first use: the Cache object is a stable handle, and every
  // operation here starts by asking for it.
  let opened: Promise<Cache> | undefined
  const open = (): Promise<Cache> => (opened ??= caches.open(name))

  const match = async (key: FileName): Promise<Response | undefined> =>
    (await open()).match(to_url(key))

  const put = async (key: FileName, response: Response): Promise<void> => {
    const cache = await open()
    await cache.put(to_url(key), response)
  }

  return {
    // Deleting the cache orphans the handle, so the next operation opens a
    // fresh one.
    async clear(): Promise<void> {
      opened = undefined
      await caches.delete(name)
    },

    // Nothing is shared between two paths, so a copy is a copy: the entry is
    // read and put again under the other name, headers and all — which is what
    // keeps its write time its own. The body streams cache to cache, unheld.
    async copy(source: FileName, dest: FileName): Promise<void> {
      const response = await match(source)
      if (!response) throw new Error(`no such file: ${source}`)
      await put(dest, response)
    },

    async delete(key: FileName): Promise<void> {
      const cache = await open()
      await cache.delete(to_url(key))
    },

    exists: (key: FileName): Promise<boolean> => match(key).then(found => !NullQ(found)),

    get: (key: FileName): Promise<StandardResponse | undefined> =>
      match(key).then(response =>
        response ? ResponseAdopt(response, { name: EntryKey(key) }) : undefined
      ),

    // Listing is every request the cache holds, matched back to its entry —
    // one that comes up empty was deleted since it was listed, which drops it.
    // The folder, the depth and the hidden entries are decided over that, and
    // the matches run together rather than one round trip at a time.
    async *list({ deep = true, hidden = false, path }: FileQuery = {}): AsyncSequence<
      [FileName, StandardResponse]
    > {
      const cache = await open()
      const members = (await cache.keys())
        .map(request => [from_url(request.url), request] as const)
        .filter(([key]) => FolderMemberQ({ deep, hidden, path }, key))
      const responses = await WaitPromises(members.map(([, request]) => cache.match(request)))

      for (const [[key], response] of Zip(members, responses)) {
        if (response) yield [key, ResponseAdopt(response, { name: key })]
      }
    },

    async move(source: FileName, dest: FileName): Promise<void> {
      await this.copy(source, dest)
      await this.delete(source)
    },

    // The other backend that cannot stream a write: the headers are the record
    // here, a Response's are fixed before its body is read, and the length is
    // the one thing a reader cannot recover — so the bytes are collected to be
    // counted. One put, and a put is atomic: nothing stages, because there is
    // no moment where this path holds half an entry.
    async set(path: FileName, value: StreamInput, type?: ContentType): Promise<FileInfo> {
      const key = EntryKey(path)
      const bytes = await UInt8Collect(value)
      // The type goes in with the rest: headers are what this backend stores,
      // so what the caller said the bytes are is kept rather than guessed back.
      const entry = ResponseCreate(bytes, { name: key, size: bytes.byteLength as Bytes, type })

      await put(key, entry)

      // Without the bytes: what a caller keeps from a write is what the entry
      // is, not another reference to what it holds.
      return entry.info
    },
  }
}
